import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, TrendingUp, Users, Award, GraduationCap, Clock } from 'lucide-react';
import { mockData } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export const InstructorTraining = () => {
  const training = mockData.instructorTraining;

  return (
    <div className="min-h-screen">
      <SEO 
        title="Become a Driving Instructor - ADI Training Liverpool"
        description="Become an Approved Driving Instructor (ADI) in Liverpool with Top Driver. £1,499 for Part 2 & Part 3 training with 30 hours in-car sessions - 3X more than big franchise schools. Start your new career today."
        keywords="become driving instructor Liverpool, ADI training Liverpool, driving instructor course Liverpool, ADI Part 2 Part 3 training, instructor training Liverpool, career change driving instructor"
        canonical="https://topdriver.co.uk/instructor-training"
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <div className="inline-block bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {training.tagline}
            </div>
            <h1 className="text-5xl sm:text-6xl font-bold text-black mb-6">
              {training.name}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              {training.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg transition-colors duration-200"
              >
                <Link to="/contact">Start Your ADI Journey - {training.price}</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-black text-black hover:bg-black hover:text-white px-8 py-6 text-lg transition-colors duration-200"
              >
                <a href="#comparison">See The Difference</a>
              </Button>
            </div>
          </div>

          {/* Comparison Card */}
          <div id="comparison" className="max-w-3xl mx-auto">
            <Card className="border-4 border-red-600">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-center text-black mb-6">
                  Why choose Top Driver for ADI Training?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="text-center p-6 bg-red-50 rounded-lg border-2 border-red-600">
                    <GraduationCap className="w-12 h-12 text-red-600 mx-auto mb-3" />
                    <p className="font-bold text-lg text-black mb-2">Top Driver (Us)</p>
                    <p className="text-3xl font-bold text-red-600 mb-2">30 Hours</p>
                    <p className="text-sm text-gray-700">In-Car Training</p>
                  </div>
                  <div className="text-center p-6 bg-gray-100 rounded-lg border-2 border-gray-300">
                    <Users className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                    <p className="font-bold text-lg text-black mb-2">Big Franchise Schools</p>
                    <p className="text-3xl font-bold text-gray-600 mb-2">10 Hours</p>
                    <p className="text-sm text-gray-600">In-Car + 20hrs Classroom</p>
                  </div>
                </div>
                <p className="text-center text-gray-700 mt-6">
                  Real teaching happens in the car, not the classroom. Get 3X more practical experience!
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">What's Included in Your {training.price}</h2>
            <p className="text-lg text-gray-600">Complete ADI Part 2 & Part 3 training package</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {training.includes.map((item) => (
              <div key={item} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-red-50 transition-colors duration-200">
                <CheckCircle2 className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <span className="text-gray-800 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">Why Train with Top Driver?</h2>
            <p className="text-lg text-gray-600">The advantages of real in-car experience</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {training.benefits.map((benefit, benefitIndex) => (
              <Card key={benefit.title} className="border-2 border-gray-200 hover:border-red-600 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center mb-4">
                    {benefitIndex === 0 && <GraduationCap className="w-6 h-6 text-white" />}
                    {benefitIndex === 1 && <Clock className="w-6 h-6 text-white" />}
                    {benefitIndex === 2 && <Award className="w-6 h-6 text-white" />}
                    {benefitIndex === 3 && <TrendingUp className="w-6 h-6 text-white" />}
                  </div>
                  <h3 className="text-2xl font-bold text-black mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Everything you need to know about becoming an ADI</p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {training.faqs.map((faq, faqIndex) => (
              <AccordionItem key={faq.question} value={`item-${faqIndex}`} className="border-2 border-gray-200 rounded-lg px-6 hover:border-red-600 transition-colors duration-200">
                <AccordionTrigger className="text-left font-semibold text-lg hover:text-red-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed pt-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-red-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to start your ADI Career?
          </h2>
          <p className="text-xl text-white mb-8 opacity-90">
            Join Top Driver's instructor training program and get 3X more in-car experience than the competition.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-white hover:bg-gray-100 text-red-600 px-8 py-6 text-lg transition-colors duration-200"
            >
              <Link to="/contact">Get Started - {training.price}</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-6 text-lg transition-colors duration-200"
            >
              <a href="tel:+447846878466">Call +44 78 468 78 466</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InstructorTraining;
